import { useMemo, useState } from 'react';

import type { CustomerProductReviewFragment, CustomerReviewStatsFragment } from '@graphql/generated';
import { useGetCatalogProductReviewsQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useReviewSorting } from './useReviewSorting';

export const useCustomerReviews = ({ catalogItemId, isOnScreen }: { catalogItemId: string; isOnScreen: boolean }) => {
  // Temporary solution to cache stats + reviews when user changes sort option
  const [cachedData, setCachedData] = useState<{ stats: Maybe<CustomerReviewStatsFragment>; reviews: CustomerProductReviewFragment[] }>({ stats: null, reviews: [] });
  const { orderingOptions, currentOrderingOption, handleOnSetReviewOrdering } = useReviewSorting();
  const { data, loading, fetchMore } = useGetCatalogProductReviewsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      catalogItemId,
      first: 4,
      orderBy: currentOrderingOption.value,
    },
    skip: !isOnScreen,
    batchMode: 'fast',
    onCompleted(data) {
      setCachedData({
        stats: data?.productCatalogItemById?.rating?.stats,
        reviews: data?.productCatalogItemById?.rating?.reviews?.pagedResults?.edges.map(edge => edge.node) || [],
      });
    },
  });

  const { hasMoreReviews, endCursor } = useMemo<{ hasMoreReviews: boolean; endCursor: Maybe<string> }>(() => {
    if (!data?.productCatalogItemById?.rating || !data?.productCatalogItemById?.rating?.reviews?.pagedResults) {
      return {
        hasMoreReviews: false,
        endCursor: undefined,
      };
    }
    const { reviews } = data.productCatalogItemById.rating;

    const pageInfo = reviews.pagedResults.pageInfo;
    const hasMoreReviews = pageInfo.hasNextPage;

    return {
      hasMoreReviews,
      endCursor: pageInfo.endCursor,
    };
  }, [data]);

  const handleOnShowMoreReviewsClick = useEventCallback(() => {
    if (hasMoreReviews) {
      fetchMore({
        variables: {
          first: 8,
          after: endCursor,
        },
      });
    }
  });

  return {
    hasMoreReviews,
    stats: cachedData.stats,
    reviews: cachedData.reviews,
    loading,
    handleOnShowMoreReviewsClick,
    orderingOptions,
    currentOrderingOption,
    handleOnSetReviewOrdering,
  };
};
